import { useEffect, useMemo, useState, useCallback } from 'react';
import { getScheduleEntries } from '../../api/schedule';
import { ArtifactModal } from '../../components/ArtifactModal/ArtifactModal';
import styles from './KnowledgeBasePage.module.css';

function extractEmbedUrl(input) {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  if (raw.startsWith('<iframe')) {
    const match = raw.match(/src="([^"]+)"/i) || raw.match(/src='([^']+)'/i);
    if (match?.[1]) return match[1];
  }
  return raw;
}

function normalizeEmbedUrl(url) {
  if (!url) return null;
  const trimmed = extractEmbedUrl(url);
  if (!trimmed) return null;
  if (trimmed.includes('youtube.com/watch')) {
    try {
      const query = new URL(trimmed).searchParams;
      const id = query.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    } catch {
      return trimmed;
    }
  }
  if (trimmed.includes('youtu.be/')) {
    const id = trimmed.split('youtu.be/')[1]?.split(/[?&]/)[0];
    if (id) return `https://www.youtube.com/embed/${id}`;
  }
  const vkMatch = trimmed.match(/video(-?\d+)_([0-9]+)/);
  if (vkMatch) {
    const oid = vkMatch[1];
    const id = vkMatch[2];
    return `https://vk.com/video_ext.php?oid=${oid}&id=${id}&hd=2`;
  }
  return trimmed;
}

function derivePreviewUrl(entry) {
  if (entry?.video_preview_url) return entry.video_preview_url;
  const source = extractEmbedUrl(entry?.video_url || entry?.recording_url || '');
  if (source.includes('youtube.com/watch')) {
    try {
      const query = new URL(source).searchParams;
      const id = query.get('v');
      if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    } catch {
      return null;
    }
  }
  if (source.includes('youtu.be/')) {
    const id = source.split('youtu.be/')[1]?.split(/[?&]/)[0];
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
}

function getDigitalTraceBadge(entry) {
  const trace = entry?.digital_trace;
  if (!trace || trace.enabled === false) return null;
  
  const statusMap = {
    'PENDING': { text: 'На проверке', type: 'pending' },
    'NOT_SUBMITTED': { text: 'Не загружен', type: 'not-submitted' },
    'PASSED': { text: 'Зачтено', type: 'passed' },
    'FAILED': { text: 'Не зачтено', type: 'failed' },
  };
  
  const status = statusMap[trace.status];
  if (!status) return null;
  
  return status;
}

function getDigitalTraceStatus(entry) {
  const trace = entry?.digital_trace;
  if (!trace) {
    return { text: 'Цифровой след', type: 'default', showButton: true };
  }
  if (trace.enabled === false) return null;

  if (trace.status === 'PASSED') return { text: 'Зачтено', type: 'passed', showButton: false };
  if (trace.status === 'FAILED') return { text: 'Не зачтено', type: 'failed', showButton: false };
  if (trace.status === 'PENDING' && trace.submission_id) {
    return { text: 'Цифровой след загружен', type: 'pending', showButton: trace.can_edit !== false };
  }
  return { text: 'Цифровой след', type: 'default', showButton: true };
}

export function KnowledgeBasePage() {
  const [entries, setEntries] = useState([]);
  const [openEntries, setOpenEntries] = useState(() => new Set());
  const [artifactEntry, setArtifactEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refreshSchedule = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    
    return getScheduleEntries()
      .then((data) => {
        if (cancelled) return;
        setEntries(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Не удалось загрузить базу знаний.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
  }, []);

  useEffect(() => {
    refreshSchedule();
  }, [refreshSchedule]);

function hasDigitalTraceData(entry) {
  const trace = entry;
  if (!trace) return false;
  
  const fields = ['video_url', 'homework_description', 'homework_template_url'];
  return fields.some(field => trace[field] != null);
}

  const videoEntries = useMemo(() => {
    return entries
      .filter((item) => item.video_url || item.recording_url)
      .filter((item) => hasDigitalTraceData(item))
      .map((item) => ({
        ...item,
        embedUrl: normalizeEmbedUrl(item.video_url || item.recording_url),
        previewUrl: derivePreviewUrl(item),
      }));
  }, [entries]);

  const toggleEntry = (entryId) => {
    setOpenEntries((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>База знаний</h1>
          <p>Видеозаписи и материалы программы в одном месте.</p>
        </div>
        <div className={styles.counter}>{videoEntries.length}</div>
      </header>

      {error ? <div className={styles.error}>{error}</div> : null}
      {loading ? (
        <div className={styles.grid}>
          {Array.from({ length: 3 }).map((_, index) => (
            <article key={`skeleton-${index}`} className={`${styles.card} ${styles.cardSkeleton}`}>
              <div className={styles.cardTop}>
                <div className={`${styles.preview} ${styles.skeletonBlock}`} />
                <div className={styles.cardInfo}>
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonTitle} />
                  <div className={styles.skeletonLineWide} />
                </div>
                <div className={styles.skeletonPill} />
              </div>
            </article>
          ))}
        </div>
      ) : null}
      {!loading && !videoEntries.length ? <div className={styles.empty}>Пока нет опубликованных видеозаписей.</div> : null}

      {!loading ? (
        <div className={styles.grid}>
          {videoEntries.map((entry) => {
            const isOpen = openEntries.has(entry.entry_id);
            const traceStatus = getDigitalTraceStatus(entry);
            
            return (
              <article key={entry.entry_id} className={styles.card}>
                <button type="button" className={styles.cardTop} onClick={() => toggleEntry(entry.entry_id)}>
                  <div className={styles.preview}>
                    {entry.previewUrl ? (
                      <img src={entry.previewUrl} alt={entry.title} loading="lazy" />
                    ) : (
                      <div className={styles.previewFallback} />
                    )}
                  </div>
                  <div className={styles.cardInfo}>
                    <div className={styles.badges}>
                      <span className={styles.track}>{entry.track_label || 'ЮФУ.Про'}</span>
                      {getDigitalTraceBadge(entry) && (
                        <span className={`${styles.track} ${styles[getDigitalTraceBadge(entry).type]}`}>
                          {getDigitalTraceBadge(entry).text}
                        </span>
                      )}
                    </div>
                    <h3>{entry.title}</h3>
                    {entry.description ? <p>{entry.description}</p> : null}
                  </div>
                  <span className={styles.expandArrow}>{isOpen ? 'Свернуть' : 'Смотреть'}</span>
                </button>
                {isOpen ? (
                  <div className={styles.cardDetails}>
                    {entry.embedUrl ? (
                      <iframe
                        title={entry.title}
                        src={entry.embedUrl}
                        className={styles.videoFrame}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : null}
                    {entry.homework_description ? (
                      <div className={styles.homeworkBlock}>
                        <strong>Задание</strong>
                        <p>{entry.homework_description}</p>
                      </div>
                    ) : null}
                    <div className={styles.actions}>
                      {(entry.recording_url || entry.video_url) ? (
                        <a href={entry.recording_url || entry.video_url} target="_blank" rel="noreferrer" className={styles.actionLink}>
                          Открыть запись
                        </a>
                      ) : null}
                      
                      {traceStatus !== null && (
                        <>
                          {traceStatus.showButton ? (
                            <button 
                              type="button" 
                              className={styles.actionLink} 
                              onClick={() => setArtifactEntry(entry)}
                            >
                              {traceStatus.text}
                            </button>
                          ) : (
                            <span className={`${styles.traceStatus} ${styles[traceStatus.type]}`}>
                              {traceStatus.text}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}

      <ArtifactModal
        isOpen={Boolean(artifactEntry)}
        onClose={() => setArtifactEntry(null)}
        onSaved={refreshSchedule}
        entry={artifactEntry}
        contextId={artifactEntry?.entry_id}
      />
    </div>
  );
}
